import React, { useContext } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle, faArrowLeft, faHome, faPaw, faTruck, faCrown, faRedo } from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { routes } from "../config/utility";

const PaymentFailure = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const { user } = useContext(UserContext);

  const isWalkPayment = queryParams.get("walk_payment") === "true";
  const isTaxiPayment = queryParams.get("taxi_payment") === "true";
  const isMembershipPayment = queryParams.get("membership_payment") === "true";

  // Determine the content based on the payment type
  let title = "Payment Failed";
  let message = "Oops! We couldn't process your payment. Please try again or contact support for assistance.";
  let backButtonText = "Back to Dashboard";
  let backButtonIcon = faHome;
  let backButtonRoute = routes.dashboard;
  let retryButtonText = "Try Again";

  if (isWalkPayment) {
    message = "We couldn't process your walk payment. Please try again or contact support for assistance.";
    backButtonText = "Back to Walks";
    backButtonIcon = faPaw;
    backButtonRoute = routes.walk;
    retryButtonText = "Retry Walk Payment";
  } else if (isTaxiPayment) {
    message = "We couldn't process your delivery payment. Please try again or contact support for assistance.";
    backButtonText = "Back to Delivery";
    backButtonIcon = faTruck;
    backButtonRoute = routes.delivery;
    retryButtonText = "Retry Delivery Payment";
  } else if (isMembershipPayment) {
    message = "We couldn't process your membership payment. Please try again or contact support for assistance.";
    backButtonText = "Back to Dashboard";
    backButtonIcon = faCrown;
    backButtonRoute = routes.dashboard;
    retryButtonText = "Retry Membership Payment";
  }

  const handleBackClick = () => {
    if (user?.isAuthenticated) {
      navigate(backButtonRoute);
    } else {
      // If not authenticated, redirect to login
      navigate(routes.login);
    }
  };

  const handleRetryClick = () => {
    // Go back to previous page to retry payment
    window.history.back();
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.5, rotateX: 20 },
    visible: { opacity: 1, scale: 1, rotateX: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: 0.3 },
    },
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100 p-4">
      <motion.div
        className="bg-white rounded-3xl shadow-2xl p-10 text-center max-w-md w-full mx-4"
        style={{ perspective: 1000 }}
        initial="hidden"
        animate="visible"
        variants={cardVariants}
      >
        <FontAwesomeIcon icon={faTimesCircle} className="text-5xl text-red-500 mb-4" />
        <h1 className="text-3xl font-bold text-dark mb-2">{title}</h1>
        <p className="text-body text-lg mb-8">
          {message}
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3">
          {/* Retry Button */}
          <motion.button
            variants={buttonVariants}
            initial="hidden"
            animate="visible"
            onClick={handleRetryClick}
            className="bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-full font-semibold transition-colors duration-200 flex items-center justify-center gap-2"
          >
            <FontAwesomeIcon icon={faRedo} />
            {retryButtonText}
          </motion.button>

          {/* Back Button */}
          <motion.button
            variants={buttonVariants}
            initial="hidden"
            animate="visible"
            onClick={handleBackClick}
            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-full font-semibold transition-colors duration-200 flex items-center justify-center gap-2"
          >
            <FontAwesomeIcon icon={backButtonIcon} />
            {backButtonText}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentFailure;
